/**
 * Client-side image processing utility for avatar uploads.
 * Resizes, compresses, and converts images before upload.
 */

export interface ProcessedImage {
  blob: Blob;
  type: string;
  width: number;
  height: number;
}

// Configuration
const MAX_DIMENSION = 512;
const JPEG_QUALITY = 0.85;
const WEBP_QUALITY = 0.80;
const MAX_FILE_SIZE_STATIC = 500 * 1024; // 500KB for JPEG/PNG/WebP
const MAX_FILE_SIZE_GIF = 2 * 1024 * 1024; // 2MB for GIFs (animated)

/**
 * Check if the browser supports WebP encoding
 */
function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

/**
 * Load an image file into an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 */
function calculateDimensions(
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number } {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }

  const aspectRatio = width / height;

  if (width > height) {
    return {
      width: maxDimension,
      height: Math.round(maxDimension / aspectRatio),
    };
  } else {
    return {
      width: Math.round(maxDimension * aspectRatio),
      height: maxDimension,
    };
  }
}

/**
 * Convert a canvas to a Blob with the specified format
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      type,
      quality
    );
  });
}

/**
 * Process an avatar image: resize, compress, and optionally convert format
 */
export async function processAvatarImage(file: File): Promise<ProcessedImage> {
  // Handle GIFs specially - don't process, just validate size
  if (file.type === 'image/gif') {
    if (file.size > MAX_FILE_SIZE_GIF) {
      throw new Error('Animated GIFs must be under 2MB');
    }
    return {
      blob: file,
      type: 'image/gif',
      width: 0, // Unknown without processing
      height: 0,
    };
  }

  // Validate input type
  const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Unsupported image format. Please use JPEG, PNG, WebP, or GIF.');
  }

  // Load the image
  const img = await loadImage(file);
  const { width, height } = calculateDimensions(
    img.naturalWidth,
    img.naturalHeight,
    MAX_DIMENSION
  );

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Use high-quality image rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Try WebP first if supported, otherwise fall back to JPEG
  const useWebP = supportsWebP();
  const outputType = useWebP ? 'image/webp' : 'image/jpeg';
  const quality = useWebP ? WEBP_QUALITY : JPEG_QUALITY;

  let blob = await canvasToBlob(canvas, outputType, quality);

  // If still too large, progressively reduce quality
  let currentQuality = quality;
  while (blob.size > MAX_FILE_SIZE_STATIC && currentQuality > 0.5) {
    currentQuality -= 0.1;
    blob = await canvasToBlob(canvas, outputType, currentQuality);
  }

  // If still too large after quality reduction, throw error
  if (blob.size > MAX_FILE_SIZE_STATIC) {
    throw new Error('Unable to compress image to under 500KB. Please try a smaller image.');
  }

  return {
    blob,
    type: outputType,
    width,
    height,
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
